import { ExclamationTriangleIcon, UsersIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useRef, useState } from "react";
import { useClickOutsideBlock } from "../../helpers/useClickOutside";
import { LoaderSpinner } from "../LoaderSpinner";
import { SelectDropdown } from "../SelectDropdown";

export const RemoveRoleModal = ({
    isOpen,
    isLoading,
    role,
    allRoles,
    cancelClick,
    submitClick,
}) => {
    const modalRef = useRef(null);
    const [newRole, setNewRole] = useState(null);
    const disabledDelete = role?.code === "ADMIN" || role?.code === "DIRECTOR";
    useClickOutsideBlock(modalRef, cancelClick);
    if (!isOpen) return null;
    return (
        <div aria-hidden="true" className="overflow-y-auto overflow-x-hidden fixed top-0 right-0 left-0 z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full">
            <div className="bg-gray-700 top-0 right-0 left-0 fixed z-40 justify-center items-center w-full md:inset-0 h-[calc(100%)] max-h-full opacity-30" />
            <div ref={modalRef} className={`relative p-5 w-full max-w-lg max-h-full bg-white border top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 rounded-md`}>
                <div className="flex justify-end">
                    <button 
                        onClick={cancelClick}
                        className="inline-flex items-center p-1 text-primary absolute"
                    >
                        <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                    </button>
                </div>
                <div className="flex flex-col gap-4 text-center sm:text-left">
                    <h2 className="text-lg leading-none font-semibold flex items-center gap-2">
                        <ExclamationTriangleIcon className="h-6 w-6 text-red-500"/>
                        <span>Delete Role</span>
                    </h2>
                    <p className="text-gray-700/90 text-sm">
                        Are you sure you want to delete the "{role?.name || ""}" role? This action cannot be undone.
                    </p>
                </div>
                {disabledDelete && (
                    <div className="space-y-4 my-4">
                        <div className="p-4 bg-red-100 border border-red-200 rounded-lg">
                            <p className="text-sm text-red-600 font-medium">
                                System roles cannot be deleted
                            </p>
                        </div>
                    </div>
                )}
                {!disabledDelete && (role?.usersAssigned > 0) && (
                    <div className="my-4">
                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <UsersIcon className="h-5 w-5 text-amber-600" />
                            <span className="text-sm text-amber-800">
                                This role is assigned to {role?.usersAssigned} users.
                            </span>
                        </div>
                        <div className="space-y-2">
                            <label className="text-sm font-medium">
                                Reassign users to:
                            </label>
                            <SelectDropdown
                                options={(allRoles || []).filter((roleItem) => roleItem.value !== role.id)}
                                value={newRole}
                                onChange={setNewRole}
                                placeholder="Select a replacement role"
                                selectClassname="bg-white"
                            />
                        </div>
                    </div> 
                )}
                <div className="flex justify-end gap-2">
                    <button 
                        onClick={() => {
                            cancelClick()
                        }}
                        className="min-w-20 inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:text-white hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:mt-0 sm:w-auto sm:text-sm"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Cancel"}
                    </button>
                    <button 
                        onClick={() => {
                            if (!isLoading) {
                                submitClick(role?.id, newRole);
                                setNewRole(null);
                            }
                        }}
                        disabled={disabledDelete || (!disabledDelete && !newRole && role?.usersAssigned > 0)} 
                        className="min-w-24 justify-center inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white disabled:opacity-50 bg-red-500 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                    >
                        {isLoading ? <LoaderSpinner width="20" height="20" border="3" colorSpinner="gray"/> : "Delete Role"}
                    </button>
                </div>
            </div>
        </div>
    )
}